import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetCommodities, dataSetCommodityConditions } from '../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../services/appFunctionsService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import CommodityHttpService from '../../../services/http/masterData/commodityHttpService';
import Screen from '../../../components/Screen';
import CommodityForm from './form/CommodityForm';
import CommodityConditionHttpService from '../../../services/http/masterData/commodityConditionHttpService';
import { setCommodityMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';
import { ConfirmationDialog, CustomTable, ProjectDialog } from '@zz2/zz2-ui';
import { IApiError } from '../../../@types/other';
import { useLocation } from 'react-router-dom';
import { RightGrantsEnum } from '../../../@types/enum/rightGrantsEnum';
import { ICommodity } from '../../../@types/model/masterData/commodity/commodity';
import { ICommodityFormValues } from '../../../@types/model/masterData/commodity/commodityFormValues';
import UserHelper from '../../../services/helper/userHelper';
import CommodityModelHelper from '../../../@types/model/masterData/commodity/commodityModelHelper';

const CommodityScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const currentUser = useAppSelector(x => x.auth.session?.user ?? null);
    const commodities = useAppSelector(x => x.masterData.commodities);
    const selectedOrganizationIds = useAppSelector(x => x.data.selectedOrganizationIds);
    const organizations = useAppSelector(x => x.masterData.organizations);
    const commodityConditions = useAppSelector(x => x.masterData.commodityConditions);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedCommodity, setSelectedCommodity] = useState<ICommodity | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [deletingCommodity, setDeletingCommodity] = useState<ICommodity | null>(null);

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        fetchData();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const fetchData = async () => {
        const isIndexedDBAvailable = !!self.indexedDB;
        if (isIndexedDBAvailable) {
            await syncMasterData(false);
        } else {
            setIsLoading(true);
            try {
                const res = await CommodityHttpService.getCommodityData();
                const res2 = await CommodityConditionHttpService.getCommodityConditionData();
                dispatch(dataSetCommodities(res.data));
                dispatch(dataSetCommodityConditions(res2.data));
            } catch (error : unknown) {
                const apiError = error as IApiError | undefined;
                generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading commodities.');
            } finally {
                setIsLoading(false);
            }
        }
    };

    const onSubmit = async (values : ICommodityFormValues) => {
        setIsLoading(true);

        try {
            const upsert = CommodityModelHelper.createUpsert(values);
            const res = await CommodityHttpService.addOrUpdateCommodity(upsert);
            const updatedList = upsertArrayElement(commodities, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetCommodities(updatedList));
            await setCommodityMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedCommodity ? 'Commodity updated successfully.' : 'Commodity added successfully.');
            onModalDismiss();
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating Commodity data.');
        } finally {
            setIsLoading(false);
        }
    };

    const removeCommodity = async () => {
        if (!deletingCommodity) return;
        const updatedCommodity = { ...deletingCommodity, isActive: false };
        setIsLoading(true);
        try {
            const res = await CommodityHttpService.deleteCommodity(updatedCommodity.id);
            const updatedList = upsertArrayElement(commodities, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetCommodities(updatedList));
            await setCommodityMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Commodity deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting commodity.');
        } finally {
            closeDeletePopup();
            setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openModal = () => {
        setIsDialogOpen(true);
    };

    const onModalDismiss = () => {
        setIsDialogOpen(false);
        setSelectedCommodity(null);
    };

    const edit = (selectedRow : ICommodity) => {
        setSelectedCommodity(selectedRow);
        openModal();
    };

    const onDelete = (selectedRow : ICommodity) : void => {
        setDeletingCommodity(selectedRow);
    };

    const closeDeletePopup = () : void => {
        setDeletingCommodity(null);
    };

    const deleteButtonTooltip = (row : ICommodity) : string => {
        let message = 'Delete';

        if (!userHasRights.deleteRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Commodity', RightGrantsEnum.Delete);
        } else if (!row.isActive) {
            message = 'Already Inactive.';
        } else if (row.isImported) {
            message = 'Cannot delete imported entries.';
        }

        return message;
    };

    const formatOrganizations = (organizationIds : Array<number>) => {
        return organizations
            .filter(x => organizationIds.some(y => y === x.id))
            .map(x => x.code)
            .toString()
            .replace(/,/g, ', ');
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const userHasRights = useMemo(() => {
        return UserHelper.userRelatedRights(location.pathname, currentUser?.rights);
    }, [currentUser, location]);

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return CommodityModelHelper.createFormValues(selectedCommodity, organizations, commodities, commodityConditions);
        }
    }, [selectedCommodity, commodities, commodityConditions, organizations, isDialogOpen]);

    const rows = useMemo(() => {
        if (userHasRights.editRight) {
            return commodities.filter(x => x.organizationIds.some(y => selectedOrganizationIds?.some(z => z === y)) || x.organizationIds.length < 1);
        }
        return commodities.filter(x => x.organizationIds.some(y => selectedOrganizationIds?.some(z => z === y)));
    }, [commodities, selectedOrganizationIds, userHasRights]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <Screen isLoading={isLoading} isScrollable={false}>
            <Card className={'fdc hfill'}>
                <CustomTable<ICommodity>
                
                    enableAdding
                    // addTooltipRightValidation={!userHasRights.addRight}
                    disableAddButton={!userHasRights.addRight || isLoading}
                    addTooltip={!userHasRights.addRight ? UserHelper.getRelatedRightToolTipMessage('Commodity', RightGrantsEnum.Add) : 'Add'}
                    addFunction={openModal}
                    // addTooltipOnClick={() : void => requestAccess('Add')}
                
                    enableEditing
                    // editTooltipRightValidation={!userHasRights.editRight}
                    disableEditButton={!userHasRights.editRight || isLoading}
                    editTooltip={!userHasRights.editRight ? UserHelper.getRelatedRightToolTipMessage('Commodity', RightGrantsEnum.Edit) : 'Edit'}
                    editFunction={edit}
                    // editTooltipOnClick={() : void => requestAccess('Edit')}

                    enableDeleting
                    // deleteTooltipRightValidation={!userHasRights.deleteRight}
                    disableDeleteButton={(row : ICommodity) : boolean => !userHasRights.deleteRight || !row.isActive || row.isImported || isLoading}
                    deleteTooltip={deleteButtonTooltip}
                    deleteFunction={onDelete}
                    // deleteTooltipOnClick={() : void => requestAccess('Delete')}

                    enableSorting
                    enableFiltering
                    enablePagination
                    columns={[
                        { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                        { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                        { title: 'Organizations', field: 'organizationIds', formatFunction: formatOrganizations, width: 300, enableFiltering: true, enableSorting: true },
                        { title: 'Stone Fruit?', field: 'isStoneFruit', type: 'boolean', enableFiltering: true, enableSorting: true },
                        { title: 'Phyto Compliant?', field: 'isPhytoCompliant', type: 'boolean', enableFiltering: true, enableSorting: true },
                        { title: 'Export Code', field: 'exportCode', enableFiltering: true, enableSorting: true },
                        { title: 'Temperature', field: 'temperature', enableFiltering: true, enableSorting: true },
                        { title: 'Temperature Regime', field: 'temperatureRegime', enableFiltering: true, enableSorting: true },
                        { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                        { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                        { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        { title: 'Imported?', field: 'isImported', type: 'boolean', enableFiltering: true, enableSorting: true },
                    ]}
                    fitWidthToPage
                    rows={rows}
                    initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                    pageSizes={[50, 150, 250, 500, 1000]}
                    pageHeight={190}
                    isActive={row => row.isActive}
                />
            </Card>
            {isDialogOpen && initialValues &&
                <ProjectDialog
                    title={selectedCommodity ? 'Edit Commodity' : 'Add Commodity'}
                    isLoadingCircular={isLoading}
                    isOpen={isDialogOpen}
                    onClose={onModalDismiss}
                    fullScreen
                >
                    <CommodityForm
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onModalDismiss}
                        isLoading={isLoading}
                        isImported={selectedCommodity?.isImported ?? false}
                    />
                </ProjectDialog>
            }
            <ConfirmationDialog
                title={'Inactivate Commodity'}
                open={!!deletingCommodity}
                description={'Are you sure you would like to set the following item to inactive?'}
                onAccept={removeCommodity}
                onClose={closeDeletePopup}
                dialogType={'red'}
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default CommodityScreen;
